import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import ApiUtils from '../../utils/apiUtils';
import base_url, {urlSearchParams} from '../../baseUrls';
import Utils from '../../utils/utils';
import {NewPermissionGate} from '../../permissions/permissionGate';
import * as allConstants from '../../constants/allConstants';
import {useDispatch} from 'react-redux';
import actions from '../../redux/actions';
import StatusRenderer from '../../pieces/grids/statusRenderer';
import ColumnDefinition from '../../pieces/grids/columnGenerator';
import {companyRules, COMPANY_ELEMENTS} from '../../permissions/companyAccessControl';
import {COMPANY_FIELDS} from '../../constants/allConstants';
import {unwrapRequestResult} from '../../utils/unwrapRequestResult';
import MyPaginatedGrid from '../../pieces/grids/paginatedGridWrapper';
import {useNavigate} from 'react-router-dom';
import { AUTO_HOLD_JOB_TYPES } from '../../constants/options/jobTypes';


const {COMPANY_FIELDS: {
  ADDRESS,
  ADDRESS_STATE,
  EMAIL,
  ID,
  MAIN_CONTACT,
  NAME,
  HIERARCHY,
  PHONE_NUMBER,
  STATUS,
  IS_SERVICE_TITAN,
  QB_EMAIL,
  CREDIT_LIMIT,
}} = allConstants;


const AllCompaniesGrid = (props) => {
  const viewController = props.viewController;
  const dispatch = useDispatch();
  const [components] = useState({
    'statusRenderer': StatusRenderer,
  });
  const navigate = useNavigate();
  const fetchParams = useMemo(() => ({url:`${base_url.api}companies`, respKey:'companies'}), []);

  const companyColumns = {
    id:  new ColumnDefinition({field: ID, hide: true}),
    name: new ColumnDefinition({field: NAME, wrapText: true, width: 300, lockPinned: true, pinned: 'left', cellStyle: {lineHeight: '1.3'},
      cellRendererParams: (params)=> {
        return {
          handleExpand: (params)=>handleDisplayCompanyDetails(params),
        };},
    }), 
    hierarchy: new ColumnDefinition({
      field: HIERARCHY,
      filter: 'agSetColumnFilter',
      filterValues: Object.values(allConstants.HIERARCHY_TYPES).map((s)=> s.value),
      cellRenderer: (params)=> {
        return params.value ? params.value : '';
      },
    }),
    status: new ColumnDefinition({field: STATUS,
      cellRenderer: 'statusRenderer', width: 200,
      filter:'agSetColumnFilter',
      filterValues: Object.values(allConstants.COMPANY_STATUSES).map((s)=> s.value),
      valueGetter: ({data})=> {
        if(data?.[COMPANY_FIELDS.IS_BLOCKED.api_name]) {
          return allConstants.COMPANY_STATUSES.BLOCKED.value;
        }
        return data?.[COMPANY_FIELDS.STATUS.api_name];
      }
    }),
    email: new ColumnDefinition({field: EMAIL}),
    mainContact: new ColumnDefinition({field: MAIN_CONTACT}),
    phoneNumber: new ColumnDefinition({field: PHONE_NUMBER,
      width: 170,
      valueGetter: ({data})=>Utils.formatPhoneNumber(data[PHONE_NUMBER.api_name])}),
    address: new ColumnDefinition({field: ADDRESS,
      headerName: 'Address',
      valueGetter: ({data})=> {
        if(!Utils.objectHasProperty(data, ADDRESS.api_name)) return '';
        return Utils.getAddressString(data[ADDRESS.api_name]);}
    }),
    state: {
      ...new ColumnDefinition({
        field: COMPANY_FIELDS.LOCATION,
        sortable: true,
        headerName: COMPANY_FIELDS.LOCATION.display_label,
        filter: 'agSetColumnFilter',
        filterValues: (params) => {
          ApiUtils.makeApiCall('GET', 'states/get-key-values').then((response) => {
            const markets = response?.data?.markets ?? [];
            params.success(markets.map((m) => m.value));
          }).catch(() => params.success([]));
        },
        valueGetter: ({data}) => data?.[COMPANY_FIELDS.LOCATION.api_name] ?? '',
      }),
      colId: 'state',
    },
    licenseNumber: new ColumnDefinition({
      field: {...COMPANY_FIELDS.STATE_LICENSE_NUMBER, ...{label: 'state license #'}},
      width: 170,
      valueGetter: ({data})=> data?.[COMPANY_FIELDS.STATE_LICENSE.api_name]?.[COMPANY_FIELDS.STATE_LICENSE_NUMBER.api_name]
    }),
    isServiceTitan: new ColumnDefinition( {
      field: IS_SERVICE_TITAN,
      headerName: 'Srv Titan?',
      filter: "agSetColumnFilter",
      filterValues: ['Yes', 'No'],
      cellRenderer: params => params.value ? "Yes" : "No",     
    }),
    qBID: new ColumnDefinition({field: COMPANY_FIELDS.QB_ID}),
    creditLimit: new ColumnDefinition({field: CREDIT_LIMIT}),
    qBEmail: new ColumnDefinition({
      field: QB_EMAIL,
    }),
    paymentMethod: new ColumnDefinition({
      field: COMPANY_FIELDS.PAYMENT_METHOD,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterValues: Object.values(allConstants.PAYMENT_METHODS).map((s) => s.value),
    }),
    allInOne: new ColumnDefinition({
      field: COMPANY_FIELDS.ALL_IN_ONE,
      sortable: true,
      headerName: COMPANY_FIELDS.ALL_IN_ONE.display_label,
      convertToBoolean: true,
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
      cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
      valueGetter: (({data}) => {
        const v = data?.[COMPANY_FIELDS.ALL_IN_ONE.api_name];
        return !!v;
      }),
    }),
    hersRequireInspection: new ColumnDefinition({
      field: COMPANY_FIELDS.HERS_REQUIRE_INSPECTION,
      sortable: true,
      headerName: COMPANY_FIELDS.HERS_REQUIRE_INSPECTION.display_label,
      convertToBoolean: true,
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
      cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
      valueGetter: (({data}) => {
        const v = data?.[COMPANY_FIELDS.HERS_REQUIRE_INSPECTION.api_name];
        return !!v;
      }),
    }),
    hersRequireDefault: new ColumnDefinition({
      field: COMPANY_FIELDS.HERS_REQUIRE_DEFAULT,
      sortable: true,
      headerName: COMPANY_FIELDS.HERS_REQUIRE_DEFAULT.display_label,
      convertToBoolean: true,
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
      cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
      valueGetter: (({data}) => {
        const v = data?.[COMPANY_FIELDS.HERS_REQUIRE_DEFAULT.api_name];
        return !!v;
      }),
    }),
    allowAiCall: new ColumnDefinition({
      field: COMPANY_FIELDS.ALLOW_AI_CALL,
      sortable: true,
      headerName: COMPANY_FIELDS.ALLOW_AI_CALL.display_label,
      filter: 'agSetColumnFilter',
      filterValues: ['Yes', 'No'],
    }),
    autoHold: new ColumnDefinition({
      field: COMPANY_FIELDS.AUTO_HOLD,
      sortable: true,
      headerName: 'Auto Hold',
      filter: 'agSetColumnFilter',
      filterValues: Object.values(AUTO_HOLD_JOB_TYPES).filter((s) => s.api_name !== 'all').map((s) => s.value),
      valueGetter: ({data}) => {
        const v = data?.[COMPANY_FIELDS.AUTO_HOLD.api_name];
        if (v == null) {
          return '';
        }
        if (Array.isArray(v)) {
          return v.filter((item) => item != null && item !== '').join(', ');
        }
        return String(v);
      },
    }),
    callbackDays: new ColumnDefinition({
      field: COMPANY_FIELDS.CALLBACK_DAYS,
      sortable: true,
      filter: 'agNumberColumnFilter',
    }),
  };
      
  const getColumns = ()=> {
    return [
      companyColumns.id,
      companyColumns.name,
      companyColumns.hierarchy,
      companyColumns.isServiceTitan,
      companyColumns.licenseNumber,
      companyColumns.mainContact,
      companyColumns.phoneNumber,
      companyColumns.address,
      companyColumns.state,
      companyColumns.status,
      companyColumns.email,
      companyColumns.qBID,
      companyColumns.qBEmail,
      companyColumns.creditLimit,
      companyColumns.paymentMethod,
      companyColumns.allInOne,
      companyColumns.hersRequireInspection,
      companyColumns.hersRequireDefault,
      companyColumns.allowAiCall,
      companyColumns.callbackDays,
      companyColumns.autoHold,
    ];
  };

  let columnDefs = useMemo(()=> {
    return getColumns();
  }, []);

  const impersonate = async (companyId)=> {
    const impersonateResult = await dispatch(actions.impersonate(companyId));
    
    if(impersonateResult?.meta?.requestStatus === 'fulfilled') {
      navigate(`${allConstants.PATH.COMPANY}?${urlSearchParams.companyId}=${companyId}`);
    }
  };

  const deactivate = async (companyId, companyName)=> {
    //console.log('will deactivate', companyId);
    const deactivateStatus = await dispatch(actions.deactivateCompany(companyId));
    unwrapRequestResult({
      result: deactivateStatus,
      successMessage: `${companyName} was DEACTIVATED. Please refresh page to see changes.`,
      errorMessage: 'Error on deactivation',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(deactivateStatus);

  };

  const activate = async (companyId, companyName)=> {
    const activateStatus = await dispatch(actions.activateCompany(companyId));
    unwrapRequestResult({
      result: activateStatus,
      successMessage: `${companyName} was ACTIVATED.`,
      errorMessage: 'Error on activation',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(activateStatus);
  };

  const block = async (companyId, companyName)=> {
    const activateStatus = await dispatch(actions.blockCompany(companyId));
    unwrapRequestResult({
      result: activateStatus,
      successMessage: `${companyName} was blocked.`,
      errorMessage: 'Error on blocking',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(activateStatus);
  };
  const unblock = async (companyId, companyName)=> {
    const activateStatus = await dispatch(actions.unblockCompany(companyId));
    unwrapRequestResult({
      result: activateStatus,
      successMessage: `${companyName} was unblocked.`,
      errorMessage: 'Error on unblocking',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(activateStatus);
  };
  const cancel = async (companyId, companyName)=> {
    const activateStatus = await dispatch(actions.cancelCompany(companyId));
    unwrapRequestResult({
      result: activateStatus,
      successMessage: `${companyName} was cancelled.`,
      errorMessage: 'Error on cancelling',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(activateStatus);
  };
  const uncancel = async (companyId, companyName)=> {
    const activateStatus = await dispatch(actions.uncancelCompany(companyId));
    unwrapRequestResult({
      result: activateStatus,
      successMessage: `${companyName} was uncancelled.`,
      errorMessage: 'Error on uncancelling',
      showSuccess: true,
    } );
    viewController.refreshOnSuccess(activateStatus);
  };

  const handleDisplayCompanyDetails = (params) => {
    navigate(`${allConstants.PATH.COMPANY}?${urlSearchParams.companyId}=${params?.node?.data.id}`);
  };

  const handleEditFinancial = (companyData) => {
    if(props.editFinancial) {
      props.editFinancial(companyData);
    }
  };

  const handleSetCompanyAdmin = (companyData) => {
    if(props.setCompanyAdmin) {
      props.setCompanyAdmin(companyData);
    }
  };

  const getContextMenuItems = (params)=> {
    // console.log('params', params?.node?.data.id, viewController.userRole);
    if(!params.node) return [];
    const companyStatus = params?.node?.data[allConstants.COMPANY_FIELDS.STATUS.api_name];
    const isBlocked = params?.node?.data[allConstants.COMPANY_FIELDS.IS_BLOCKED.api_name];
    // const isSignedUp = Utils.notEqualsIgnoreCase(companyStatus, allConstants.COMPANY_STATUSES.NOT_SIGNED.value);
    const actions = {
      impersonate: {
        name: 'Impersonate',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> impersonate(params?.node?.data.id),
        cssClasses: ['redFont', 'bold'],
      },
      viewComapny: {
        name:  params?.node?.data?.[COMPANY_FIELDS.STATUS.api_name] === allConstants.COMPANY_STATUSES.PENDING.value ? 'View/Verify' : 'View',
        cssClasses: ['bold'],
        disabled: viewController.selectedMultipleRows(),
        action: () => {handleDisplayCompanyDetails(params);},
      },
      deactivate: {
        name: 'Deactivate',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> deactivate(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      activate: {
        name: 'Activate',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> activate(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      block: {
        name: 'Block company',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> block(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      unblock: {
        name: 'Unblock company',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> unblock(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      cancel: {
        name: 'Cancel company',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> cancel(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      uncancel: {
        name: 'Uncancel company',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> uncancel(params?.node?.data.id, params?.node?.data[COMPANY_FIELDS.NAME.api_name]),
      },
      editFinancialInfo: {
        name: 'Edit Financial Info',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> handleEditFinancial(params?.node?.data),
      },
      setCompanyAdmin: {
        name: 'Set/Update Company Admin',
        disabled: viewController.selectedMultipleRows(),
        action: ()=> handleSetCompanyAdmin(params?.node?.data),
      }
    };

    const standard = [
      {
        name:  'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel(
          {onlySelected: true}
        )
      },
      'copy',
    ];
    const separator=[
      'separator'
    ];
    const companyActions = [actions.viewComapny];
    let needSecondSeparator = false;

    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.financeInfoEdit]
    })) {
      needSecondSeparator = true;
      companyActions.push(actions.editFinancialInfo);
    }

    companyActions.push('separator');

    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.impersonate],
    })) {
      needSecondSeparator = true;
      companyActions.push(actions.impersonate);
      companyActions.push('separator');
    }

    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.editStatus],
    })) {
      needSecondSeparator = true;
      if(companyStatus === allConstants.COMPANY_STATUSES.ACTIVE.value) {
        companyActions.push(actions.deactivate);
      } else if(companyStatus === allConstants.COMPANY_STATUSES.NOT_ACTIVE.value){
        companyActions.push(actions.activate);
      }
    }
    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.editStatus], 
    })) {
      needSecondSeparator = true;
      if(!isBlocked) {
        companyActions.push(actions.block);
      } else {
        companyActions.push(actions.unblock);
      }
    }
    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.editStatus], 
    })) {
      needSecondSeparator = true;
      if(companyStatus !== allConstants.COMPANY_STATUSES.CANCELLED.value) {
        companyActions.push(actions.cancel);
      } else {
        companyActions.push(actions.uncancel);
      }
    }
    if(NewPermissionGate({
      user: viewController.user,
      elementPermissions: companyRules[COMPANY_ELEMENTS.editStatus],
    })) {
      needSecondSeparator = true;
      companyActions.push(actions.setCompanyAdmin);
    }

    return needSecondSeparator ? [...companyActions, ...separator, ...standard] : [...companyActions, ...standard];
  };

  return (
    <MyPaginatedGrid columnDefs={columnDefs}
      components={components}
      ls_name={'all_companies_grid'}
      rowHeight={65}
      colDefOverrides={{ autoHeight: false }}
      gridOptionsOverrides={{ animateRows: false, rowBuffer: 25 }}
      onCellDoubleClicked={(params)=> handleDisplayCompanyDetails(params)}
      registerGrid={viewController.setGridToParent}
      registerTempGrid={viewController.setTempGridToParent}    
      contextMenu={getContextMenuItems}
      fetchParams={fetchParams}
      onFetchFail={viewController.onFetchFail}
      pagination={props.pagination}
      refreshTimestamp={viewController.refreshTimestamp}
    />
  );
};

AllCompaniesGrid.propTypes = {
  pagination: PropTypes.instanceOf(Object).isRequired,
  viewController: PropTypes.instanceOf(Object).isRequired,
  editFinancial: PropTypes.func.isRequired,
  setCompanyAdmin: PropTypes.func.isRequired
};
export default AllCompaniesGrid;
